import { buildUrl, CLIENT_APP, refreshSession } from './http';
import { session } from './session';

/**
 * Realtime notification signals over Server-Sent Events (`GET /api/v1/platform/notifications/stream`).
 *  - fetch streaming, not EventSource: the bearer token goes in the Authorization header (never in the URL, where it would
 *    end up in proxy access logs and browser history).
 *  - Events carry ids only (`{"id":…}`); the caller refetches the notification list.
 *  - 401 → one refresh, then reconnect; refresh rejected → stop (the session is gone). Other failures → backoff 1 s … 30 s.
 *  - The server closes the stream when the access token expires; we reconnect at once with a fresh token.
 */
export type StreamEvent = { type: 'ready' } | { type: 'notification'; id: number } | { type: 'resync' };
export type StreamStatus = 'connecting' | 'open' | 'closed';

export const STREAM_PATH = '/platform/notifications/stream';

/** Incremental SSE parser: feed text chunks, get complete events. Comments (`: ping`) and unknown events are ignored. */
export function createSseParser() {
  let buffer = '';
  return (chunk: string): StreamEvent[] => {
    buffer += chunk.replace(/\r\n?/g, '\n');
    const out: StreamEvent[] = [];
    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let event = 'message';
      const data: string[] = [];
      for (const line of block.split('\n')) {
        if (!line || line.startsWith(':')) continue;
        const i = line.indexOf(':');
        const field = i < 0 ? line : line.slice(0, i);
        const value = i < 0 ? '' : line.slice(i + 1).replace(/^ /, '');
        if (field === 'event') event = value;
        else if (field === 'data') data.push(value);
      }
      if (event === 'ready') out.push({ type: 'ready' });
      else if (event === 'resync') out.push({ type: 'resync' });
      else if (event === 'notification') {
        try {
          const id = Number((JSON.parse(data.join('\n')) as { id?: unknown }).id);
          if (Number.isFinite(id) && id > 0) out.push({ type: 'notification', id });
        } catch {
          /* malformed event: ignore */
        }
      }
    }
    return out;
  };
}

/** Delay before reconnect attempt `n` (0-based): 1, 2, 4 … 30 s, ±20 % jitter so tabs don't reconnect in lockstep. */
export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(30_000, 1000 * 2 ** Math.max(0, attempt));
  return Math.round(base * (0.8 + random() * 0.4));
}

export interface StreamOptions {
  onEvent: (e: StreamEvent) => void;
  onStatus?: (s: StreamStatus) => void;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

/** Opens the stream and keeps it open until `stop()`. One instance per tab. */
export function openNotificationStream({ onEvent, onStatus, fetchImpl }: StreamOptions) {
  const doFetch = fetchImpl ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  let stopped = false;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let controller: AbortController | undefined;

  const schedule = (ms: number) => {
    if (stopped) return;
    onStatus?.('connecting');
    timer = setTimeout(() => void connect(), ms);
  };

  const connect = async () => {
    if (stopped) return;
    onStatus?.('connecting');
    // Token about to expire (or missing after a reload): refresh first so the stream is not cut after a few seconds.
    if (!session.getAccessToken() || session.getAccessExpiresAt() - Date.now() < 10_000) {
      if (!session.getRefreshToken()) return stop();
      await refreshSession();
      if (stopped) return;
      if (!session.getAccessToken()) return session.getRefreshToken() ? schedule(backoffMs(attempt++)) : stop();
    }
    controller = new AbortController();
    let res: Response;
    try {
      res = await doFetch(buildUrl(STREAM_PATH), {
        headers: { Accept: 'text/event-stream', 'X-Client-App': CLIENT_APP, Authorization: `Bearer ${session.getAccessToken()}` },
        signal: controller.signal,
        cache: 'no-store',
      });
    } catch {
      return schedule(backoffMs(attempt++));
    }
    if (res.status === 401) {
      if (await refreshSession()) return schedule(0);
      return session.getRefreshToken() ? schedule(backoffMs(attempt++)) : stop();
    }
    if (!res.ok || !res.body) {
      // 429 (too many tabs) or a proxy error: polling keeps working; retry slowly.
      return schedule(res.status === 429 ? 60_000 : backoffMs(attempt++));
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const parse = createSseParser();
    let openedAt = 0;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        for (const e of parse(decoder.decode(value, { stream: true }))) {
          if (e.type === 'ready') {
            openedAt = Date.now();
            attempt = 0;
            onStatus?.('open');
          }
          onEvent(e);
        }
      }
    } catch {
      /* aborted or network drop */
    }
    if (stopped) return;
    // Normal end (token expiry) after a healthy connection → reconnect now; otherwise back off.
    schedule(openedAt && Date.now() - openedAt > 30_000 ? 0 : backoffMs(attempt++));
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    clearTimeout(timer);
    controller?.abort();
    onStatus?.('closed');
  };

  void connect();
  return { stop };
}
